import { Workflow, Terminal } from "lucide-react";

import { StatusIndicator, CellStatus } from "./StatusIndicator";

export interface StatusRendererProps {
  status: CellStatus;
  cellType?: "workflow" | "code_execution" | "default";
  errorMessage?: string | null;
  className?: string;
}

const getCellTypeIcon = (cellType: string) => {
  switch (cellType) {
    case "workflow":
      return <Workflow className="h-4 w-4" />;
    case "code_execution":
      return <Terminal className="h-4 w-4" />;
    default:
      return null;
  }
};

const getStatusText = (cellType: string, status: CellStatus) => {
  if (status === CellStatus.FAILED) {
    switch (cellType) {
      case "workflow":
        return "Workflow failed";
      case "code_execution":
        return "Execution failed";
      default:
        return "Failed";
    }
  }

  if (status === CellStatus.RUNNING) {
    switch (cellType) {
      case "workflow":
        return "Running workflow...";
      case "code_execution":
        return "Executing...";
      default:
        return "Running...";
    }
  }

  if (status === CellStatus.QUEUED) {
    return "Queued";
  }
};

export const StatusRenderer = ({
  status,
  cellType = "default",
  errorMessage,
  className = "",
}: StatusRendererProps) => {
  const customIcon = getCellTypeIcon(cellType);
  const statusText = getStatusText(cellType, status);

  return (
    <div
      className={`flex h-full w-full flex-col items-center justify-center gap-1 p-2 ${className}`}
    >
      <div className="flex items-center gap-1">
        <StatusIndicator
          status={status}
          icon={customIcon}
          tooltipText={statusText}
          showTooltip={false}
        />
        <span className="text-xs text-gray-600">{statusText}</span>
      </div>
      {status === CellStatus.FAILED && errorMessage && (
        <div className="mt-1 text-xs text-red-500">{errorMessage}</div>
      )}
    </div>
  );
};

export const useStatusRenderer = (status: CellStatus) => status !== CellStatus.COMPLETED;
